import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { useSuppliers } from "@/components/containers/ContainerDocuments";

export const CONDITION_ACTIONS = {
  DAMAGE: "Mark damaged",
  RELEASE_DAMAGED: "Damaged → sellable",
  QUARANTINE: "Quarantine",
  RELEASE_QUARANTINE: "Release quarantine",
  WRITE_OFF_DAMAGED: "Write off damaged",
};

const newKey = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);

function LotRow({ lot, onDone, supCode }) {
  const [p, setP] = useState({ action: "DAMAGE", boxes: "", reason: "" });
  const [key, setKey] = useState(newKey);
  const [busy, setBusy] = useState(false);
  const apply = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/inventory/lots/${lot.id}/condition`, { ...p, boxes: Number(p.boxes), idempotency_key: key });
      toast.success("Lot condition updated");
      setP({ action: "DAMAGE", boxes: "", reason: "" });
      setKey(newKey());
      onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-1 border-b border-border pb-2 text-xs last:border-0" data-testid={`lot-row-${lot.id}`}>
      <p className="text-charcoal" data-testid={`lot-counts-${lot.id}`}>
        <span className="font-medium">{lot.sku} · {supCode || "no supplier"}</span> · received {lot.received_boxes} · available <span className="font-semibold">{lot.remaining_boxes}</span> · dispatched {lot.allocated_boxes} · damaged {lot.damaged} · quarantined {lot.quarantined} · unsellable {lot.unsellable}
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <select value={p.action} onChange={(e) => setP((x) => ({ ...x, action: e.target.value }))} className="h-8 rounded-md border border-border bg-white px-2" data-testid={`lot-action-${lot.id}`}>
          {Object.entries(CONDITION_ACTIONS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <Input type="number" min="1" value={p.boxes} onChange={(e) => setP((x) => ({ ...x, boxes: e.target.value }))} placeholder="Boxes" className="h-8 w-24 bg-white" data-testid={`lot-boxes-${lot.id}`} />
        <Input value={p.reason} onChange={(e) => setP((x) => ({ ...x, reason: e.target.value }))} placeholder="Reason" className="h-8 w-48 bg-white" data-testid={`lot-reason-${lot.id}`} />
        <Button size="sm" variant="outline" className="h-8 text-xs" disabled={busy || !p.boxes || !p.reason.trim()} onClick={apply} data-testid={`lot-apply-${lot.id}`}>Apply</Button>
      </div>
      {lot.condition_events.slice(0, 3).map((e) => (
        <p key={e.idempotency_key} className="text-muted-foreground" data-testid={`lot-event-${e.idempotency_key}`}>
          {e.at?.slice(0, 16).replace("T", " ")} · {CONDITION_ACTIONS[e.action]} {e.boxes} · {e.reason} · {e.by_email}
        </p>
      ))}
    </div>
  );
}

// ID-055: post-receipt damage/quarantine on the originating container lot (synchronised with Florida stock)
export function ContainerLots({ cid }) {
  const [lots, setLots] = useState(null);
  const suppliers = useSuppliers();
  const load = () => api.get(`/admin/inventory/lots?container_id=${cid}`).then(({ data }) => setLots(data)).catch(() => setLots([]));
  useEffect(() => { load(); }, [cid]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!lots) return null;
  return (
    <div data-testid="container-lots-block">
      <p className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Received lots & condition</p>
      <div className="mt-2 space-y-2">
        {lots.length ? lots.map((l) => <LotRow key={l.id} lot={l} onDone={load} supCode={suppliers.find((s) => s.id === l.supplier_id)?.code} />) : <p className="text-xs text-muted-foreground" data-testid="lots-empty">Lots are created when the container is received.</p>}
      </div>
    </div>
  );
}
